import React, { useState, useEffect } from 'react';
import { hasAnyRole } from '../../utils/roleHelper';
import { useRanks } from '../../hooks/useRanks';
import PersonnelSelect from './PersonnelSelect';

const PaginationControls = ({ currentPage, totalItems, pageSize, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  if (totalPages <= 1) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fff', borderTop: '1px solid #e2e8f0', fontSize: 13 }}>
      <div style={{ color: '#6b7280' }}>
        Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} items
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}
          style={{ padding: '5px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: currentPage === 1 ? '#f1f5f9' : '#fff', color: currentPage === 1 ? '#94a3b8' : '#334155', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 12 }}>
          Previous
        </button>
        <span style={{ padding: '5px 12px', fontWeight: 700, color: '#1e293b', fontSize: 12 }}>Page {currentPage} of {totalPages}</span>
        <button disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)}
          style={{ padding: '5px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: currentPage === totalPages ? '#f1f5f9' : '#fff', color: currentPage === totalPages ? '#94a3b8' : '#334155', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 12 }}>
          Next
        </button>
      </div>
    </div>
  );
};

const SalaryStructureTab = ({ api, user, armyTypeId }) => {
  const [subView, setSubView] = useState('grades'); // 'grades' or 'personnel_history'
  const [grades, setGrades] = useState([]);
  const [personnelStructures, setPersonnelStructures] = useState([]);
  const [personnelList, setPersonnelList] = useState([]);

  // Pagination states
  const [gradePage, setGradePage] = useState(1);
  const gradePageSize = 10;
  const [historyPage, setHistoryPage] = useState(1);
  const historyPageSize = 10;

  // Form states
  const [form, setForm] = useState({ rank: '', grade_name: '', base_salary: '' });
  const [assignForm, setAssignForm] = useState({ army_personnel_id: '', salary_grade_id: '', base_salary: '', effective_from: new Date().toISOString().slice(0, 10) });

  const [editing, setEditing] = useState(null); // grade ID being edited
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });

  const canEdit = hasAnyRole(user, ['admin', 'finance_director']);

  useEffect(() => {
    setGradePage(1);
    setHistoryPage(1);
    fetchGrades();
    fetchPersonnelStructures();
    fetchPersonnelList();
  }, [armyTypeId]);

  const fetchGrades = async () => {
    try {
      const res = await api.get(`/salary_grades?army_type_id=${armyTypeId || ''}`);
      const list = res?.salary_grades || res?.data?.salary_grades || (Array.isArray(res) ? res : []);
      setGrades(list);
    } catch (e) { console.error('Error fetching grades:', e); }
  };

  const fetchPersonnelStructures = async () => {
    try {
      const res = await api.get(`/salary_structures?army_type_id=${armyTypeId || ''}`);
      const list = res?.salary_structures || res?.data?.salary_structures || (Array.isArray(res) ? res : []);
      setPersonnelStructures(list);
    } catch (e) { console.error('Error fetching personnel structures:', e); }
  };

  const fetchPersonnelList = async () => {
    try {
      const res = await api.get(`/army_personnel?army_type_id=${armyTypeId || ''}`);
      const list = res?.army_personnel || res?.data?.army_personnel || (Array.isArray(res) ? res : []);
      setPersonnelList(list);
    } catch (e) { console.error('Error fetching personnel list:', e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg({ text: '', type: '' });

    const effectiveGradeName = form.grade_name.trim() || form.rank;
    const existingSameGrade = grades.find(g => g.rank === form.rank && (g.grade_name || g.rank) === effectiveGradeName && g.id !== editing);
    if (!editing && existingSameGrade) {
      setLoading(false);
      setMsg({
        text: `⚠ Salary grade "${effectiveGradeName}" already exists for rank "${form.rank}" in this Force (Salary: $${existingSameGrade.base_salary}). Click Edit on that row to modify it or enter a different Grade Name.`,
        type: 'warning'
      });
      document.getElementById(`grade-row-${existingSameGrade.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      let res;
      const payload = { ...form, army_type_id: armyTypeId };
      if (editing) {
        res = await api.put(`/salary_grades/${editing}`, { salary_grade: payload });
      } else {
        res = await api.post('/salary_grades', { salary_grade: payload });
      }

      if (res?.response && !res.response.ok) {
        const errs = res.data?.errors || [res.data?.error || 'Failed to save salary grade'];
        setMsg({ text: Array.isArray(errs) ? errs.join(', ') : errs, type: 'error' });
      } else {
        setMsg({
          text: editing ? `✅ "${form.rank}" grade updated to $${form.base_salary}` : `✅ Salary grade for "${form.rank}" created ($${form.base_salary})`,
          type: 'success'
        });
        setForm({ rank: '', grade_name: '', base_salary: '' });
        setEditing(null);
        fetchGrades();
      }
    } catch (e) {
      setMsg({ text: e.message || 'Error saving grade', type: 'error' });
    } finally { setLoading(false); }
  };

  const handleAssignStructure = async (e) => {
    e.preventDefault();
    if (!assignForm.army_personnel_id) return setMsg({ text: 'Please select a personnel member', type: 'warning' });
    setLoading(true);
    setMsg({ text: '', type: '' });

    try {
      const selectedGrade = grades.find(g => String(g.id) === String(assignForm.salary_grade_id));
      const payload = {
        army_personnel_id: assignForm.army_personnel_id,
        salary_structure: {
          salary_grade_id: assignForm.salary_grade_id || null,
          base_salary: assignForm.base_salary || selectedGrade?.base_salary || 0,
          effective_from: assignForm.effective_from,
          status: 'active'
        }
      };

      const res = await api.post('/salary_structures', payload);
      if (res?.response && !res.response.ok) {
        setMsg({ text: res.data?.errors?.join(', ') || 'Failed to assign salary structure', type: 'error' });
      } else {
        setMsg({ text: '✅ Personnel salary structure updated & versioned successfully.', type: 'success' });
        setAssignForm({ army_personnel_id: '', salary_grade_id: '', base_salary: '', effective_from: new Date().toISOString().slice(0, 10) });
        fetchPersonnelStructures();
      }
    } catch (e) {
      setMsg({ text: e.message || 'Error assigning structure', type: 'error' });
    } finally { setLoading(false); }
  };

  const handleEdit = (g) => {
    setEditing(g.id);
    setForm({ rank: g.rank, grade_name: g.grade_name || '', base_salary: g.base_salary });
    setMsg({ text: `Editing "${g.rank}" — modify values and click Update.`, type: 'info' });
    document.getElementById('salary-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDelete = async (id, rank) => {
    if (!confirm(`Delete salary grade for "${rank}"? This may affect payroll calculations.`)) return;
    try {
      const res = await api.delete(`/salary_grades/${id}`);
      if (res?.response && !res.response.ok) {
        setMsg({ text: res.data?.error || 'Could not delete — grade may be in use', type: 'error' });
      } else {
        setMsg({ text: `Deleted grade for "${rank}"`, type: 'success' });
        fetchGrades();
      }
    } catch (e) {
      setMsg({ text: 'Could not delete — grade may be in use', type: 'error' });
    }
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ rank: '', grade_name: '', base_salary: '' });
    setMsg({ text: '', type: '' });
  };

  const { ranks: dynamicRanks } = useRanks(api);

  const defaultRanks = [
    'S/Guud:', 'S/Gaas:', 'G/Sare:', 'Dhame', '2X/le:', 'X/Le:',
    'K/S:', 'K/L:', 'K/K:', 'D/le:', 'G/le:'
  ];

  const rankLabels = {
    'S/Guud:': 'S/Guud: (Sareeye Guud - General)',
    'S/Gaas:': 'S/Gaas: (Sareeye Gaas - Major General)',
    'G/Sare:': 'G/Sare: (Gaashaanle Sare - Colonel)',
    'K/S:': 'K/S: (Kormeere Sare - Senior Inspector)',
    'K/L:': 'K/L: (Kormeere Labaad - Inspector)',
    'K/K:': 'K/K: (Kormeere Keliya - Junior Inspector)',
    'G/le:': 'G/le: (Gaashaanle - Major)',
    'Dhame': 'Dhame (Dhame - Captain)',
    '2X/le:': '2X/le: (Laba Xiddigle - 1st Lieutenant)',
    'X/Le:': 'X/Le: (Xiddigle - 2nd Lieutenant)',
    'D/le:': 'D/le: (Dabaliye - Private / Soldier)'
  };

  const ranks = dynamicRanks && dynamicRanks.length > 0
    ? dynamicRanks.map(r => r.name || r).filter(n => n !== 'N/A')
    : defaultRanks;

  const getRankLabel = (r) => {
    const found = dynamicRanks?.find(item => (item.name || item) === r);
    if (found?.somali_name && found.somali_name !== 'N/A') {
      return `${found.name} (${found.somali_name})`;
    }
    return rankLabels[r] || r;
  };

  const msgStyle = {
    success: { bg: '#dcfce7', border: '#86efac', color: '#166534' },
    error:   { bg: '#fee2e2', border: '#fca5a5', color: '#991b1b' },
    warning: { bg: '#fef9c3', border: '#fde047', color: '#854d0e' },
    info:    { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af' },
  };

  return (
    <div>
      {/* View Switcher Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setSubView('grades')}
            style={{
              padding: '8px 16px', borderRadius: 6, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
              background: subView === 'grades' ? '#1b4332' : '#e2e8f0',
              color: subView === 'grades' ? '#fff' : '#4b5563'
            }}
          >
            📊 Rank Salary Grades ({grades.length})
          </button>
          <button
            onClick={() => setSubView('personnel_history')}
            style={{
              padding: '8px 16px', borderRadius: 6, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
              background: subView === 'personnel_history' ? '#1b4332' : '#e2e8f0',
              color: subView === 'personnel_history' ? '#fff' : '#4b5563'
            }}
          >
            👤 Personnel Salary History ({personnelStructures.length})
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, color: '#166534' }}>
        <strong>ℹ Salary Structure Management</strong> — {subView === 'grades' ? 'Configure base salaries for Ranks & Grades.' : 'View & assign version-controlled salary structures for individual personnel.'}
        {!canEdit && <span style={{ color: '#dc2626', marginLeft: 8 }}>⚠ You have read-only access to this tab.</span>}
      </div>

      {/* Global Message Banner */}
      {msg.text && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', borderRadius: 6, fontSize: 13, fontWeight: 500,
          background: msgStyle[msg.type]?.bg || '#f9fafb',
          border: `1px solid ${msgStyle[msg.type]?.border || '#e2e8f0'}`,
          color: msgStyle[msg.type]?.color || '#374151'
        }}>
          {msg.text}
        </div>
      )}

      {/* SUB-VIEW 1: RANK SALARY GRADES */}
      {subView === 'grades' && (
        <>
          {/* Add/Edit Form */}
          {canEdit && (
            <div id="salary-form" style={{ background: '#fff', border: `2px solid ${editing ? '#3b82f6' : '#e2e8f0'}`, borderRadius: 8, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: editing ? '#1d4ed8' : '#111827' }}>
                {editing ? `✏️ Editing: ${form.rank}` : 'Add Salary Grade'}
              </h3>
              <p style={{ margin: '0 0 14px', fontSize: 12, color: '#6b7280' }}>
                Ranks can have multiple salary grades (e.g. Grade-14, Grade-15). Specify a Grade Name to differentiate grades for the same rank.
              </p>

              <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {/* Rank Selector */}
                <div style={{ flex: '1 1 180px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Rank *</label>
                  <select
                    value={form.rank}
                    onChange={e => setForm({ ...form, rank: e.target.value })}
                    required
                    disabled={!!editing}
                    style={{ width: '100%', padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, background: editing ? '#f8fafc' : '#fff' }}
                  >
                    <option value="">Select Rank</option>
                    {ranks.map(r => {
                      const count = grades.filter(g => g.rank === r).length;
                      return (
                        <option key={r} value={r} style={{ color: '#111827' }}>
                          {getRankLabel(r)}{count > 0 ? ` (${count} grade${count > 1 ? 's' : ''})` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Grade Name */}
                <div style={{ flex: '1 1 130px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Grade Name</label>
                  <input
                    value={form.grade_name}
                    onChange={e => setForm({ ...form, grade_name: e.target.value })}
                    placeholder="e.g. Grade-16"
                    style={{ width: '100%', padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                  />
                </div>

                {/* Base Salary */}
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Base Salary (USD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.base_salary}
                    onChange={e => setForm({ ...form, base_salary: e.target.value })}
                    placeholder="0.00"
                    required
                    style={{ width: '100%', padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                  />
                </div>

                {/* Effective From */}
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Effective Date</label>
                  <input
                    type="date"
                    value={form.effective_from || new Date().toISOString().slice(0, 10)}
                    onChange={e => setForm({ ...form, effective_from: e.target.value })}
                    style={{ width: '100%', padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                  />
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      padding: '10px 20px', background: editing ? '#2563eb' : '#1b4332',
                      color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13,
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Saving...' : editing ? '💾 Update' : '+ Add Grade'}
                  </button>
                  {editing && (
                    <button
                      type="button"
                      onClick={cancelEdit}
                      style={{ padding: '10px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* Grades Table */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Salary Grades ({grades.length})</h3>
              <button onClick={fetchGrades} style={{ padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 5, background: '#f8fafc', cursor: 'pointer', fontSize: 12 }}>🔄 Refresh</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['#', 'Rank', 'Grade Name', 'Base Salary (USD)', ...(canEdit ? ['Actions'] : [])].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#374151', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grades.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>
                        No salary grades configured yet. Add grades using the form above.
                      </td>
                    </tr>
                  )}
                  {grades.slice((gradePage - 1) * gradePageSize, gradePage * gradePageSize).map((g, i) => {
                    const globalIndex = (gradePage - 1) * gradePageSize + i + 1;
                    const isBeingEdited = editing === g.id;
                    return (
                      <tr
                        key={g.id}
                        id={`grade-row-${g.id}`}
                        style={{
                          background: isBeingEdited ? '#eff6ff' : i % 2 === 0 ? '#fff' : '#f9fafb',
                          outline: isBeingEdited ? '2px solid #3b82f6' : 'none',
                          transition: 'background 0.2s'
                        }}
                      >
                        <td style={{ padding: '10px 14px', color: '#9ca3af', fontSize: 12 }}>{globalIndex}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, color: isBeingEdited ? '#1d4ed8' : '#111827' }}>
                          {isBeingEdited ? '✏️ ' : ''}{g.rank}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#6b7280' }}>{g.grade_name || '—'}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 800, color: '#059669', fontSize: 15 }}>
                          ${parseFloat(g.base_salary || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        {canEdit && (
                          <td style={{ padding: '10px 14px' }}>
                            <button
                              onClick={() => handleEdit(g)}
                              style={{ marginRight: 6, padding: '4px 12px', background: isBeingEdited ? '#dbeafe' : '#3b82f6', color: isBeingEdited ? '#1d4ed8' : '#fff', border: isBeingEdited ? '1px solid #93c5fd' : 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                            >
                              {isBeingEdited ? '✏️ Editing...' : 'Edit'}
                            </button>
                            <button
                              onClick={() => handleDelete(g.id, g.rank)}
                              style={{ padding: '4px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                            >
                              Delete
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationControls
              currentPage={gradePage}
              totalItems={grades.length}
              pageSize={gradePageSize}
              onPageChange={setGradePage}
            />
          </div>
        </>
      )}

      {/* SUB-VIEW 2: PERSONNEL SALARY HISTORY */}
      {subView === 'personnel_history' && (
        <>
          {/* Assignment Form */}
          {canEdit && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Assign / Upgrade Personnel Salary Structure</h3>
              <p style={{ margin: '0 0 14px', fontSize: 12, color: '#6b7280' }}>
                Assigning a new salary grade automatically archives previous active structure rates and increments the version number.
              </p>

              <form onSubmit={handleAssignStructure} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {/* Select Personnel */}
                <div style={{ flex: '1 1 280px' }}>
                  <PersonnelSelect
                    personnelList={personnelList}
                    personnelStructures={personnelStructures}
                    value={assignForm.army_personnel_id}
                    required={true}
                    onChange={(pId, selectedPersonnel) => {
                      const matchingGrade = grades.find(g => g.rank === selectedPersonnel?.rank);
                      setAssignForm({
                        ...assignForm,
                        army_personnel_id: pId,
                        salary_grade_id: matchingGrade?.id || '',
                        base_salary: matchingGrade?.base_salary || ''
                      });
                    }}
                  />
                </div>

                {/* Salary Grade */}
                <div style={{ flex: '1 1 180px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Assign Salary Grade</label>
                  <select
                    value={assignForm.salary_grade_id}
                    onChange={e => {
                      const gId = e.target.value;
                      const selectedGrade = grades.find(g => String(g.id) === String(gId));
                      setAssignForm({
                        ...assignForm,
                        salary_grade_id: gId,
                        base_salary: selectedGrade?.base_salary || assignForm.base_salary
                      });
                    }}
                    style={{ width: '100%', padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                  >
                    <option value="">Select Salary Grade</option>
                    {grades.map(g => (
                      <option key={g.id} value={g.id}>
                        {g.rank} — {g.grade_name || 'Standard'} (${g.base_salary})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Base Salary */}
                <div style={{ flex: '1 1 130px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Custom Base Salary ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={assignForm.base_salary}
                    onChange={e => setAssignForm({ ...assignForm, base_salary: e.target.value })}
                    placeholder="0.00"
                    style={{ width: '100%', padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                  />
                </div>

                {/* Effective From */}
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Effective From *</label>
                  <input
                    type="date"
                    value={assignForm.effective_from}
                    onChange={e => setAssignForm({ ...assignForm, effective_from: e.target.value })}
                    required
                    style={{ width: '100%', padding: '9px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
                  />
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      padding: '10px 20px', background: '#1b4332', color: '#fff', border: 'none', borderRadius: 6,
                      fontWeight: 700, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Assigning...' : 'Assign Structure'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Structures History Table */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Personnel Salary History ({personnelStructures.length})</h3>
              <button onClick={fetchPersonnelStructures} style={{ padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: 5, background: '#f8fafc', cursor: 'pointer', fontSize: 12 }}>🔄 Refresh</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['#', 'Personnel', 'Rank / Grade', 'Base Salary', 'Effective Period', 'Version', 'Status'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#374151', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {personnelStructures.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>
                        No personnel salary structures assigned yet. Use the form above to assign salary structures to personnel.
                      </td>
                    </tr>
                  )}
                  {personnelStructures.slice((historyPage - 1) * historyPageSize, historyPage * historyPageSize).map((st, i) => {
                    const globalIndex = (historyPage - 1) * historyPageSize + i + 1;
                    return (
                      <tr key={st.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                        <td style={{ padding: '10px 14px', color: '#9ca3af', fontSize: 12 }}>{globalIndex}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, color: '#111827' }}>
                          {st.army_personnel ? `${st.army_personnel.first_name || ''} ${st.army_personnel.last_name || ''}` : '—'}
                          {st.army_personnel?.army_id && <span style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>ID: {st.army_personnel.army_id}</span>}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#374151' }}>
                          {st.salary_grade ? `${st.salary_grade.rank} (${st.salary_grade.grade_name || 'Standard'})` : st.army_personnel?.rank || 'Custom'}
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: 800, color: '#059669', fontSize: 14 }}>
                          ${parseFloat(st.base_salary || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#4b5563', fontSize: 12 }}>
                          {st.effective_from ? new Date(st.effective_from).toLocaleDateString() : '—'}
                          {st.effective_to ? ` to ${new Date(st.effective_to).toLocaleDateString()}` : ' (Present)'}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#6b7280', fontSize: 12, fontWeight: 700 }}>
                          v{st.version || 1}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                            background: st.status === 'active' ? '#dcfce7' : '#f3f4f6',
                            color: st.status === 'active' ? '#166534' : '#6b7280'
                          }}>
                            {st.status || 'active'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationControls
              currentPage={historyPage}
              totalItems={personnelStructures.length}
              pageSize={historyPageSize}
              onPageChange={setHistoryPage}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default SalaryStructureTab;
